import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import useWidgetData from '../hooks/useWidgetData';
import { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import _ from 'underscore';

/**
 * ResourceOverview Widget - Shows total number of resources aggregated per resource cluster
 * Displays icons horizontally with resource counts
 * Uses the same data as ResourceListWidgetGrouped
 */
const ResourceOverviewWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  const { startDate, endDate } = useFilterStoreWithMomentDates();

  // Stable filters to avoid re-fetch loops when filters is undefined or recreated
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build a non-mutating config with updated dates merged into the request body; memoize to keep reference stable
  const finalConfig = useMemo(() => {
    const dataSource = config?.dataSource || {};
    const baseBody = dataSource.body || {};
    
    // Only add dates if both are present to avoid calling .format on null/undefined
    const bodyWithDates = (startDate && endDate) ? {
      ...baseBody,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    } : baseBody;

    return {
      ...config,
      dataSource: {
        ...dataSource,
        method: dataSource.method || 'POST',
        body: bodyWithDates
      }
    };
  }, [config, startDate, endDate]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const rawData = useMemo(() => data?.ResourceList || data?.data || [], [data]);

  // Group resource list by resource_cluster (same logic as ResourceListWidgetGrouped)
  const groupResourceList = useCallback((data, groupBy) => {
    const groupedAssets = [];
    let lastGroupBy = "";
    let lastGroup = {};

    let pdata = _.clone(data);

    pdata.sort((a, b) => {
      // Special case: if either is "Other", handle differently
      if (a[groupBy] === "Other") return 1;  // Other goes last
      if (b[groupBy] === "Other") return -1; // Other goes last
      
      // Normal alphabetical comparison for non-Other groups
      if (a[groupBy] < b[groupBy]) {
        return -1;
      }
      if (a[groupBy] > b[groupBy]) {
        return 1;
      }
      return 0;
    });

    for (const asset of pdata) {
      if (asset[groupBy] !== lastGroupBy) {
        lastGroupBy = asset[groupBy];
        lastGroup = {
          [groupBy]: asset[groupBy],
          units: 0,
          cost: 0,
          insights: 0,
          subRows: []
        };
        groupedAssets.push(lastGroup);
      }

      lastGroup.units += asset.units;

      if (asset.cost > -1) {
        lastGroup.cost += asset.cost;
      }

      if (asset.insights > -1) {
        lastGroup.insights += asset.insights;
      }

      lastGroup.subRows.push(asset);
    }

    return groupedAssets;
  }, []);

  // Aggregate resources by resource cluster
  const clusterResources = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return [];
    }

    // Group by resource_cluster
    const resourceList = groupResourceList(rawData, "resource_cluster");

    // Get selected clusters and order from config
    const selectedClusters = config?.selectedClusters || [];
    const clusterOrder = config?.clusterOrder || [];
    
    // Convert to array format
    const allClusters = resourceList
      .map(cluster => ({
        cluster: cluster.resource_cluster,
        totalResources: cluster.units || 0
      }))
      .filter(item => item.totalResources > 0); // Only show clusters with resources

    // Filter by selected clusters if specified, otherwise show all
    const filteredClusters = selectedClusters.length > 0
      ? allClusters.filter(item => selectedClusters.includes(item.cluster))
      : allClusters;

    // Sort by configured order if specified, otherwise use default order
    if (clusterOrder.length > 0) {
      // Create a map for quick lookup
      const orderMap = new Map(clusterOrder.map((cluster, index) => [cluster, index]));
      
      return filteredClusters.sort((a, b) => {
        const aIndex = orderMap.get(a.cluster);
        const bIndex = orderMap.get(b.cluster);
        
        // If both are in the order, sort by order
        if (aIndex !== undefined && bIndex !== undefined) {
          return aIndex - bIndex;
        }
        // If only a is in order, it comes first
        if (aIndex !== undefined) return -1;
        // If only b is in order, it comes first
        if (bIndex !== undefined) return 1;
        // If neither is in order, maintain original order
        return 0;
      });
    }

    // Default sorting if no order specified
    const defaultOrder = ['Compute', 'Database', 'Storage', 'Networking', 'Other'];
    return filteredClusters.sort((a, b) => {
      const aIndex = defaultOrder.indexOf(a.cluster);
      const bIndex = defaultOrder.indexOf(b.cluster);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.cluster.localeCompare(b.cluster);
    });
  }, [rawData, groupResourceList, config?.selectedClusters, config?.clusterOrder]);

  // Icon mapping for resource clusters (same as ResourceChangesOverviewWidget)
  const getClusterIcon = (cluster) => {
    switch (cluster) {
      case 'Compute':
        return icon({ name: 'computer', family: 'classic', style: 'regular' });
      case 'Database':
        return icon({ name: 'database', family: 'classic', style: 'regular' });
      case 'Storage':
        return icon({ name: 'box-archive', family: 'classic', style: 'regular' });
      case 'Networking':
        return icon({ name: 'wifi', family: 'classic', style: 'regular' });
      case 'Other':
        return icon({ name: 'circle', family: 'classic', style: 'regular' });
      default:
        return icon({ name: 'circle', family: 'classic', style: 'regular' });
    }
  };

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      {/* Title visible only in PDF/print mode */}
      <h2 className="resource-overview-pdf-title text-lg font-semibold text-text widget-title mb-4 pl-6 pb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {config.title || 'Resource overview'}
      </h2>
      <div 
        className="h-full flex flex-col items-center justify-center p-4"
        style={{ minHeight: '100px' }}
      >
        {clusterResources.length > 0 ? (
          <div className="flex flex-row items-center justify-center gap-20 flex-wrap">
            {clusterResources.map((item) => {
              return (
                <div
                  key={item.cluster}
                  className="flex flex-col items-center gap-2 cursor-default"
                >
                  <FontAwesomeIcon
                    icon={getClusterIcon(item.cluster)}
                    className="text-text-2" style={{ fontSize: "32px" }}
                  />
                  <span className="text-lg font-semibold text-text">
                    {item.totalResources}
                  </span>
                  <span className="text-sm text-text-2">
                    {item.cluster}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div 
            className="flex-1 flex items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <p className="text-text-2 text-sm">
              {!rawData || rawData.length === 0 ? 'No data available' : 'No resources available'}
            </p>
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

ResourceOverviewWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    showTitle: PropTypes.bool,
    selectedClusters: PropTypes.arrayOf(PropTypes.string),
    clusterOrder: PropTypes.arrayOf(PropTypes.string),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default ResourceOverviewWidget;
